// src/HotelRoomsScreen.js
import React, { useEffect, useMemo, useState } from "react";
import { hotelApi } from "./hotelApi";

const emptyForm = {
  id: 0,
  room_no: "",
  room_type: "",
  floor: "",
  capacity: 1,
  base_rate: "",
  is_active: 1,
};

export default function HotelRoomsScreen({ onBack }) {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");

  const [showForm, setShowForm] = useState(false);
  const [isCustomType, setIsCustomType] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const showMsg = (text) => {
    setMsg(text);
    clearTimeout(showMsg._t);
    showMsg._t = setTimeout(() => setMsg(""), 4000);
  };

  const roomTypes = useMemo(() => {
    return Array.from(
      new Set(
        (rooms || [])
          .map((r) => String(r.room_type || "").trim())
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [rooms]);

  const filteredRooms = useMemo(() => {
    const q = search.trim().toLowerCase();

    return (rooms || []).filter((r) => {
      const matchesSearch =
        !q ||
        String(r.room_no || "").toLowerCase().includes(q) ||
        String(r.room_type || "").toLowerCase().includes(q) ||
        String(r.floor || "").toLowerCase().includes(q);

      const matchesType =
        typeFilter === "All" || String(r.room_type || "") === typeFilter;

      return matchesSearch && matchesType;
    });
  }, [rooms, search, typeFilter]);

  const loadRooms = async () => {
    try {
      setLoading(true);
      const res = await hotelApi.get("/rooms");
      const rows = Array.isArray(res.data?.rows) ? res.data.rows : [];
      setRooms(rows);
    } catch (err) {
      console.error(err);
      const serverMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Failed to load rooms.";
      showMsg(`❌ ${serverMsg}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRooms();
  }, []);

  const resetForm = () => {
    setForm(emptyForm);
    setIsCustomType(false);
  };

  const openAddForm = () => {
    resetForm();
    setShowForm(true);
  };

  const closeForm = () => {
    resetForm();
    setShowForm(false);
  };

  const openEditForm = (room) => {
    const roomType = String(room.room_type || "").trim();

    setForm({
      id: Number(room.id || 0),
      room_no: String(room.room_no || ""),
      room_type: roomType,
      floor: String(room.floor || ""),
      capacity: Number(room.capacity || 1),
      base_rate: room.base_rate ?? "",
      is_active: Number(room.is_active) === 1 ? 1 : 0,
    });

    setIsCustomType(roomType ? !roomTypes.includes(roomType) : false);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const saveRoom = async (e) => {
    if (e?.preventDefault) e.preventDefault();

    const payload = {
      ...form,
      room_no: String(form.room_no || "").trim(),
      room_type: String(form.room_type || "").trim(),
      floor: String(form.floor || "").trim(),
      capacity: Math.max(1, Number(form.capacity || 1)),
      base_rate:
        String(form.base_rate ?? "").trim() === ""
          ? 0
          : Number(form.base_rate),
      is_active: Number(form.is_active) === 1 ? 1 : 0,
    };

    if (!payload.room_no || !payload.room_type) {
      showMsg("❌ Room number and room type are required.");
      return;
    }

    try {
      setSaving(true);

      if (payload.id) {
        await hotelApi.put(`/rooms/${payload.id}`, payload);
        showMsg("✅ Room updated.");
      } else {
        await hotelApi.post("/rooms", payload);
        showMsg("✅ Room created.");
      }

      closeForm();
      await loadRooms();
    } catch (err) {
      console.error(err);
      const serverMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        `Save failed (${err?.response?.status || "no status"})`;
      showMsg(`❌ ${serverMsg}`);
    } finally {
      setSaving(false);
    }
  };

  const deleteRoom = async (id, roomNo) => {
    const ok = window.confirm(
      `Delete room ${roomNo || ""}? This cannot be undone.`
    );
    if (!ok) return;

    try {
      setLoading(true);
      await hotelApi.delete(`/rooms/${id}`);
      showMsg("🗑 Room deleted.");
      await loadRooms();
    } catch (err) {
      console.error(err);
      const serverMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        `Delete failed (${err?.response?.status || "no status"})`;
      showMsg(`❌ ${serverMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const isOccupied = (room) => {
    const v = room?.is_occupied;
    return v === 1 || v === "1" || v === true;
  };

  const statusBadge = (room) => {
    const active = Number(room?.is_active) === 1;
    if (!active) {
      return <span style={{ ...styles.badge, ...styles.badgeMuted }}>Inactive</span>;
    }
    return <span style={{ ...styles.badge, ...styles.badgeGreen }}>Active</span>;
  };

  const occupiedBadge = (room) => {
    return isOccupied(room) ? (
      <span style={{ ...styles.badge, ...styles.badgeRed }}>Occupied</span>
    ) : (
      <span style={{ ...styles.badge, ...styles.badgeGreen }}>Available</span>
    );
  };

  const typeSelectValue = useMemo(() => {
    if (isCustomType) return "__custom__";
    return form.room_type || "";
  }, [isCustomType, form.room_type]);

  return (
    <div style={styles.page}>
      <div style={styles.topBar}>
        <button style={styles.backTopBtn} onClick={onBack}>
          ◀ Back to Menu
        </button>
        <div style={styles.pageLabel}>Page: hotel-rooms</div>
      </div>

      <div style={styles.container}>
        <div style={styles.headerRow}>
          <div>
            <h1 style={styles.title}>Hotel Rooms</h1>
            <p style={styles.subtitle}>Register rooms and view occupancy.</p>
          </div>

          <div style={styles.headerActions}>
            <button style={styles.secondaryBtn} onClick={loadRooms} disabled={loading}>
              ↻ Refresh
            </button>
            <button style={styles.primaryBtn} onClick={openAddForm}>
              + Add Room
            </button>
          </div>
        </div>

        {msg ? <div style={styles.alert}>{msg}</div> : null}

        <div style={styles.filtersCard}>
          <div style={styles.filtersGrid}>
            <div>
              <label style={styles.label}>Search</label>
              <input
                style={styles.input}
                placeholder="room no, type, floor..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div>
              <label style={styles.label}>Room Type</label>
              <select
                style={styles.input}
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="All">All</option>
                {roomTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={styles.label}>Status</label>
              <div style={styles.helperText}>
                Occupied status depends on active stays in the backend.
              </div>
            </div>
          </div>
        </div>

        {showForm && (
          <div style={styles.formCard}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>{form.id ? "Edit Room" : "Add Room"}</h3>
            </div>

            <form onSubmit={saveRoom}>
              <div style={styles.formGrid}>
                <div>
                  <label style={styles.label}>Room No *</label>
                  <input
                    style={styles.input}
                    placeholder="e.g. 101"
                    value={form.room_no}
                    onChange={(e) => setForm({ ...form, room_no: e.target.value })}
                  />
                </div>

                <div>
                  <label style={styles.label}>Room Type *</label>

                  {!isCustomType ? (
                    <select
                      style={styles.input}
                      value={typeSelectValue}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "__custom__") {
                          setIsCustomType(true);
                          setForm({ ...form, room_type: "" });
                        } else {
                          setIsCustomType(false);
                          setForm({ ...form, room_type: v });
                        }
                      }}
                    >
                      <option value="">Select type</option>
                      {roomTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                      <option value="__custom__">+ Add new type</option>
                    </select>
                  ) : (
                    <>
                      <input
                        style={styles.input}
                        placeholder="e.g. Standard"
                        value={form.room_type}
                        onChange={(e) =>
                          setForm({ ...form, room_type: e.target.value })
                        }
                      />
                      <button
                        type="button"
                        style={styles.linkBtn}
                        onClick={() => {
                          setIsCustomType(false);
                          setForm({ ...form, room_type: "" });
                        }}
                      >
                        Back to list
                      </button>
                    </>
                  )}
                </div>

                <div>
                  <label style={styles.label}>Floor</label>
                  <input
                    style={styles.input}
                    placeholder="e.g. 1"
                    value={form.floor}
                    onChange={(e) => setForm({ ...form, floor: e.target.value })}
                  />
                </div>

                <div>
                  <label style={styles.label}>Capacity</label>
                  <input
                    style={styles.input}
                    type="number"
                    min="1"
                    value={form.capacity}
                    onChange={(e) => setForm({ ...form, capacity: e.target.value })}
                  />
                </div>

                <div>
                  <label style={styles.label}>Base Rate</label>
                  <input
                    style={styles.input}
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="e.g. 1500"
                    value={form.base_rate}
                    onChange={(e) => setForm({ ...form, base_rate: e.target.value })}
                  />
                </div>

                <div>
                  <label style={styles.label}>Active</label>
                  <select
                    style={styles.input}
                    value={Number(form.is_active) === 1 ? "1" : "0"}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        is_active: e.target.value === "1" ? 1 : 0,
                      })
                    }
                  >
                    <option value="1">Yes</option>
                    <option value="0">No</option>
                  </select>
                </div>
              </div>

              <div style={styles.formActions}>
                <button
                  type="button"
                  style={styles.secondaryBtn}
                  onClick={closeForm}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button type="submit" style={styles.primaryBtn} disabled={saving}>
                  {saving ? "Saving..." : form.id ? "Update Room" : "Save Room"}
                </button>
              </div>
            </form>
          </div>
        )}

        <div style={styles.tableCard}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>Rooms</h3>
            <div style={styles.roomCount}>{filteredRooms.length} room(s)</div>
          </div>

          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>ID</th>
                  <th style={styles.th}>Room No</th>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Floor</th>
                  <th style={styles.th}>Capacity</th>
                  <th style={styles.th}>Base Rate</th>
                  <th style={styles.th}>Occupied</th>
                  <th style={styles.th}>Active</th>
                  <th style={styles.th}>Action</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td style={styles.emptyTd} colSpan="9">
                      Loading rooms...
                    </td>
                  </tr>
                ) : filteredRooms.length === 0 ? (
                  <tr>
                    <td style={styles.emptyTd} colSpan="9">
                      No rooms found. Click Add Room to register your first room.
                    </td>
                  </tr>
                ) : (
                  filteredRooms.map((room) => (
                    <tr key={room.id}>
                      <td style={styles.td}>{room.id}</td>
                      <td style={styles.td}>{room.room_no}</td>
                      <td style={styles.td}>{room.room_type}</td>
                      <td style={styles.td}>{room.floor || "—"}</td>
                      <td style={styles.td}>{room.capacity}</td>
                      <td style={styles.td}>{room.base_rate}</td>
                      <td style={styles.td}>{occupiedBadge(room)}</td>
                      <td style={styles.td}>{statusBadge(room)}</td>
                      <td style={styles.td}>
                        <div style={styles.actionRow}>
                          <button
                            style={styles.editBtn}
                            onClick={() => openEditForm(room)}
                          >
                            Edit
                          </button>
                          <button
                            style={styles.deleteBtn}
                            onClick={() => deleteRoom(room.id, room.room_no)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div style={styles.bottomRow}>
          <button style={styles.secondaryBtn} onClick={onBack}>
            ◀ Back
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "#f5f7fb",
    padding: 18,
    boxSizing: "border-box",
    fontFamily: "Arial, sans-serif",
    color: "#14213d",
  },

  topBar: {
    background: "#f7f9fc",
    border: "1px solid #dfe5ee",
    borderRadius: 18,
    padding: "10px 12px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },

  pageLabel: {
    fontSize: 14,
    color: "#4b5563",
    fontWeight: 700,
  },

  backTopBtn: {
    background: "#2563eb",
    color: "#fff",
    border: "none",
    borderRadius: 12,
    padding: "10px 16px",
    fontWeight: 700,
    cursor: "pointer",
  },

  container: {
    background: "#f8fafc",
    border: "1px solid #dfe5ee",
    borderRadius: 24,
    padding: 18,
  },

  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
    marginBottom: 14,
  },

  title: {
    margin: 0,
    fontSize: 22,
    fontWeight: 800,
  },

  subtitle: {
    margin: "6px 0 0 0",
    fontSize: 14,
    color: "#475569",
    fontWeight: 600,
  },

  headerActions: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },

  primaryBtn: {
    background: "#2563eb",
    color: "#fff",
    border: "none",
    borderRadius: 12,
    padding: "11px 16px",
    fontWeight: 700,
    cursor: "pointer",
    minWidth: 120,
  },

  secondaryBtn: {
    background: "#fff",
    color: "#111827",
    border: "1px solid #d1d5db",
    borderRadius: 12,
    padding: "11px 16px",
    fontWeight: 700,
    cursor: "pointer",
    minWidth: 110,
  },

  alert: {
    marginBottom: 14,
    padding: "12px 14px",
    background: "#eef4ff",
    border: "1px solid #c7d7fe",
    borderRadius: 12,
    fontWeight: 700,
  },

  filtersCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 18,
    padding: 14,
    marginBottom: 16,
  },

  filtersGrid: {
    display: "grid",
    gridTemplateColumns: "1.3fr 1fr 1fr",
    gap: 14,
  },

  formCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 18,
    padding: 16,
    marginBottom: 16,
  },

  tableCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 18,
    overflow: "hidden",
  },

  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "14px 14px 10px 14px",
    borderBottom: "1px solid #eef2f7",
  },

  cardTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 800,
  },

  roomCount: {
    fontSize: 13,
    color: "#475569",
    fontWeight: 700,
  },

  label: {
    display: "block",
    marginBottom: 6,
    fontSize: 14,
    fontWeight: 700,
  },

  helperText: {
    minHeight: 44,
    display: "flex",
    alignItems: "center",
    color: "#475569",
    fontSize: 13,
    fontWeight: 600,
  },

  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "11px 12px",
    borderRadius: 12,
    border: "1px solid #d1d5db",
    outline: "none",
    fontSize: 14,
    background: "#fff",
  },

  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 14,
  },

  formActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 16,
    flexWrap: "wrap",
  },

  linkBtn: {
    marginTop: 8,
    border: "none",
    background: "transparent",
    color: "#2563eb",
    padding: 0,
    cursor: "pointer",
    fontWeight: 700,
  },

  tableWrap: {
    width: "100%",
    overflowX: "auto",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: 980,
  },

  th: {
    textAlign: "left",
    padding: "12px 14px",
    fontSize: 13,
    fontWeight: 800,
    color: "#0f172a",
    background: "#f8fafc",
    borderBottom: "1px solid #e5e7eb",
  },

  td: {
    padding: "12px 14px",
    fontSize: 14,
    borderBottom: "1px solid #eef2f7",
    verticalAlign: "middle",
  },

  emptyTd: {
    padding: "18px 14px",
    fontSize: 15,
    color: "#475569",
    fontWeight: 600,
  },

  badge: {
    display: "inline-block",
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
  },

  badgeGreen: {
    background: "#dcfce7",
    color: "#166534",
  },

  badgeRed: {
    background: "#fee2e2",
    color: "#991b1b",
  },

  badgeMuted: {
    background: "#e5e7eb",
    color: "#374151",
  },

  actionRow: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },

  editBtn: {
    background: "#fff",
    color: "#111827",
    border: "1px solid #d1d5db",
    borderRadius: 10,
    padding: "8px 12px",
    cursor: "pointer",
    fontWeight: 700,
  },

  deleteBtn: {
    background: "#fff5f5",
    color: "#b91c1c",
    border: "1px solid #ef4444",
    borderRadius: 10,
    padding: "8px 12px",
    cursor: "pointer",
    fontWeight: 700,
  },

  bottomRow: {
    marginTop: 14,
  },
};